import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TituloDTO } from '../../../../models/dashboard/titulo.model';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-lista-despesas',
  templateUrl: './lista-despesas.component.html',
  styleUrls: ['./lista-despesas.component.scss'],
})
export class ListaDespesasComponent implements OnInit, OnDestroy {

  @Input() despesas: TituloDTO[] = [];
  @Input() loading = false;
  @Input() totalRecords = 0;
  @Input() first = 0;
  @Input() rows = 10;
  @Input() tipoTela: 'ENTRADA' | 'SAIDA' = 'SAIDA';

  @Output() pageChanged = new EventEmitter<PaginatorState>();
  @Output() sortChanged = new EventEmitter<{ field: string; order: number }>();
  @Output() tituloClicado = new EventEmitter<TituloDTO>();

  private lastSortField = '';
  private lastSortOrder = 0;

  labels = {
    pessoa: '',
    numero_titulo: '',
    vencimento: '',
    status: '',
    registro_recebidos: '',
    registro_pagos: '',
    recebido: '',
    pago_label: '',
    devolvido: '',
    saldo: '',
    valor_total: '',
    aberto: '',
    pago_status: '',
    cancelado: '',
    nenhum_resultado: '',
    lista_despesas: '',
    itens_por_pagina: '',
  };

  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  private langChangeSub!: Subscription;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.labels = {
      pessoa: this.translate.instant('contas-cobrar.labels.pessoa'),
      numero_titulo: this.translate.instant('contas-cobrar.labels.numero_titulo'),
      vencimento: this.translate.instant('contas-cobrar.labels.vencimento'),
      status: this.translate.instant('contas-cobrar.labels.status'),
      registro_recebidos: this.translate.instant('contas-cobrar.labels.registro_recebidos'),
      registro_pagos: this.translate.instant('contas-cobrar.labels.registro_pagos'),
      recebido: this.translate.instant('contas-cobrar.labels.recebido'),
      pago_label: this.translate.instant('contas-cobrar.labels.pago_label'),
      devolvido: this.translate.instant('contas-cobrar.labels.valor_devolvido'),
      saldo: this.translate.instant('contas-cobrar.labels.saldo'),
      valor_total: this.translate.instant('contas-cobrar.labels.valor_total'),
      aberto: this.translate.instant('contas-cobrar.labels.aberto'),
      pago_status: this.translate.instant('contas-cobrar.labels.pago'),
      cancelado: this.translate.instant('contas-cobrar.labels.cancelado'),
      nenhum_resultado: this.translate.instant('contas-pagar-receber.labels.nenhum_resultado'),
      lista_despesas: this.translate.instant('contas-a-pagar.labels.lista_despesas'),
      itens_por_pagina: this.translate.instant('contas-pagar-receber.labels.itens_por_pagina'),
    };
  }

  // --- Getters dinamicos ---

  get labelRegistro(): string {
    return this.tipoTela === 'ENTRADA' ? this.labels.registro_recebidos : this.labels.registro_pagos;
  }

  get labelPagoRecebido(): string {
    return this.tipoTela === 'ENTRADA' ? this.labels.recebido : this.labels.pago_label;
  }

  // --- Eventos ---

  onPageChange(event: PaginatorState): void {
    this.pageChanged.emit(event);
  }

  /** Alteracao no seletor de "Itens por pagina" — volta para a primeira pagina */
  onRowsPerPageChange(): void {
    this.pageChanged.emit({
      first: 0,
      rows: this.rows,
      page: 0,
      pageCount: Math.ceil(this.totalRecords / this.rows),
    });
  }

  onTituloClick(item: TituloDTO): void {
    this.tituloClicado.emit(item);
  }

  onSort(event: any): void {
    if (event.field && (event.field !== this.lastSortField || event.order !== this.lastSortOrder)) {
      this.lastSortField = event.field;
      this.lastSortOrder = event.order;
      this.sortChanged.emit({
        field: event.field,
        order: event.order,
      });
    }
  }

  // --- Helpers da tabela ---

  /** Soma valorMovimento (ou valor como fallback) de cada tipoDeCobranca */
  calcularTotalPago(titulo: TituloDTO): number {
    if (!titulo.tipoDeCobranca || titulo.tipoDeCobranca.length === 0) return 0;
    return titulo.tipoDeCobranca.reduce((sum, pag) => sum + (pag.valorMovimento || pag.valor || 0), 0);
  }

  /** Extrai string da situacao (pode ser string ou objeto com .enum) */
  private getSituacao(titulo: TituloDTO): string {
    const sit = titulo.situacaoDeCobranca;
    if (!sit) return 'ABERTO';
    if (typeof sit === 'object' && (sit as any)?.enum) return (sit as any).enum.toString().toUpperCase();
    return sit.toString().toUpperCase();
  }

  /** Verifica se o titulo esta aberto */
  isAberto(titulo: TituloDTO): boolean {
    return this.getSituacao(titulo) === 'ABERTO';
  }

  /** Verifica se o titulo esta pago */
  isPago(titulo: TituloDTO): boolean {
    return this.getSituacao(titulo) === 'PAGO';
  }

  /** Verifica se o titulo esta cancelado */
  isCancelado(titulo: TituloDTO): boolean {
    return this.getSituacao(titulo) === 'CANCELADO';
  }

  /** Verifica se o titulo tem devolucao */
  hasDevolvido(titulo: TituloDTO): boolean {
    return (titulo.valorDevolvido || 0) > 0;
  }

  /** Formata data de vencimento */
  formatarVencimento(vencimento: string): string {
    if (!vencimento) return '-';
    try {
      const date = new Date(vencimento);
      if (isNaN(date.getTime())) return vencimento;
      const lang = this.translate.currentLang || 'pt-BR';
      const locale = lang === 'es' ? 'es-PY' : 'pt-BR';
      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return vencimento;
    }
  }

  /** Retorna o simbolo da moeda */
  getMoedaSimbolo(moeda: any): string {
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    if (!moedaStr) return '';
    switch (moedaStr) {
      case 'GUARANI': return 'Gs';
      case 'REAL': return 'R$';
      case 'DOLAR': return '$';
      case 'PESO': return '$';
      default: return '';
    }
  }

  /**
   * Formata um valor monetario de acordo com a moeda informada.
   * Replica o padrao de formatarValorMonetario da tela de Movimentacao
   * (Intl.NumberFormat + simbolo + casas decimais condicionais).
   */
  formatarValor(valor: number | null | undefined, moeda: any): string {
    if (valor == null || isNaN(valor as number)) return '-';
    const moedaStr = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    const configuracoes: Record<string, { locale: string; simbolo: string; decimais: boolean }> = {
      GUARANI: { locale: 'es-PY', simbolo: 'Gs', decimais: false },
      REAL: { locale: 'pt-BR', simbolo: 'R$', decimais: true },
      DOLAR: { locale: 'en-US', simbolo: '$', decimais: true },
      PESO: { locale: 'es-AR', simbolo: '$', decimais: true },
    };
    const config = configuracoes[moedaStr] ?? configuracoes['GUARANI'];
    const options: Intl.NumberFormatOptions = config.decimais
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    const valorFormatado = new Intl.NumberFormat(config.locale, options).format(Math.abs(valor as number));
    return `${config.simbolo} ${valorFormatado}`;
  }
}
